import { Dayjs } from 'dayjs';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetApartmentByCityListQuery } from '../api/apartment/apartmentApiSlice';
import { stationDateTime } from '../utils/stationTime';

/**
 * - `not_needed`: nothing picked yet, or the picked apartment's clock is the
 *   one the search already ran on, so the search result answers for it;
 * - `checking` / `failed`: the re-check is in flight / did not answer — the
 *   create stays blocked, and a failure is never read as "available";
 * - `available` / `unavailable`: the answer on the apartment's own clock.
 */
export type PickedApartmentAvailability = 'not_needed' | 'checking' | 'failed' | 'available' | 'unavailable';

interface Args {
    city: string | null | undefined;
    checkIn: Dayjs | null | undefined;
    checkOut: Dayjs | null | undefined;
    guests: number;
    useCase: string;
    pickedAssetId: string | null | undefined;
    /** The zone of the picked apartment's location — the one the booking is built on. */
    pickedZone: string;
    /** The zone the search window was built on (the city's shared zone). */
    searchZone: string;
}

/**
 * #3219: the apartment search by city runs before an apartment — and so a
 * location — is picked, on the zone the city's locations share. The booking
 * is then built on the picked apartment's own clock, and the server
 * re-validates it there. Where the two clocks differ, the search's "free"
 * says nothing about the window actually booked, so the picked apartment is
 * searched again on its own clock (the same `apartments/city/…` request the
 * page made, built in the apartment's zone) and must be in that result.
 * With one clock (every Berlin tenant) nothing is re-requested.
 */
export const usePickedApartmentAvailability = ({
    city,
    checkIn,
    checkOut,
    guests,
    useCase,
    pickedAssetId,
    pickedZone,
    searchZone,
}: Args): PickedApartmentAvailability => {
    const needed = !!(pickedAssetId && city && checkIn && checkOut && guests) && pickedZone !== searchZone;
    const { data, isFetching, isError } = useGetApartmentByCityListQuery(
        needed && city && checkIn && checkOut
            ? {
                city,
                fromDate: stationDateTime(checkIn, undefined, pickedZone).format(),
                toDate: stationDateTime(checkOut, undefined, pickedZone).format(),
                numberOfGuests: guests,
                useCase,
            }
            : skipToken,
    );
    if (!needed) return 'not_needed';
    if (isFetching) return 'checking';
    if (isError || !Array.isArray(data)) return 'failed';
    return (data as { assetId?: string }[]).some((apartment) => apartment.assetId === pickedAssetId)
        ? 'available'
        : 'unavailable';
};
